import React from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const SocialProof = () => {
  const certifications = [
    {
      name: 'React Certified Developer',
      issuer: 'Meta',
      icon: 'Award',
      color: 'from-blue-500 to-blue-600'
    },
    {
      name: 'AWS Solutions Architect',
      issuer: 'Amazon Web Services',
      icon: 'Cloud',
      color: 'from-orange-500 to-orange-600'
    },
    {
      name: 'MySQL Database',
      issuer: 'Oracle',
      icon: 'Database',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      name: 'Node.js Certified Developer',
      issuer: 'OpenJS Foundation',
      icon: 'Code',
      color: 'from-green-600 to-green-700'
    }
  ];

  const partnerships = [
    {
      name: 'Vercel',
      type: 'Deployment Partner',
      logo: 'Zap',
      description: 'Official partner for high-performance web deployments'
    },
    {
      name: 'Stripe/Razorpay',
      type: 'Payment Integration',
      logo: 'CreditCard',
      description: 'Certified for secure payment processing solutions globally and in India'
    },
    {
      name: 'MongoDB',
      type: 'Database Solutions',
      logo: 'Database',
      description: 'Certified partner for scalable database architectures'
    },
    {
      name: 'Figma',
      type: 'Design Partner',
      logo: 'Palette',
      description: 'Professional design and prototyping partnership'
    }
  ];

  const capabilities = [
    {
      value: '15+',
      label: 'Technologies Mastered',
      icon: 'Code2',
      color: 'text-primary'
    },
    {
      value: '24/7',
      label: 'Support Available',
      icon: 'Clock',
      color: 'text-accent'
    },
    {
      value: '100%',
      label: 'Quality Commitment',
      icon: 'Shield',
      color: 'text-success'
    },
    {
      value: 'Ready',
      label: 'To Start Today',
      icon: 'Rocket',
      color: 'text-warning'
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Shield" size={16} />
            <span>Technical Excellence</span>
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Built on Expertise & Partnerships
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Our team brings certified expertise and strategic technology partnerships, ready to deliver enterprise-quality solutions from day one.
          </p>
        </motion.div>

        {/* Capability Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
        >
          {capabilities.map((capability, index) => (
            <motion.div
              key={capability.label}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Icon name={capability.icon} size={24} className={capability.color} />
              </div>
              <div className={`text-3xl font-bold mb-2 ${capability.color}`}>{capability.value}</div>
              <div className="text-sm text-text-secondary">{capability.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* Certifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">
            Professional Certifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-2xl p-6 text-center hover:shadow-brand-lg transition-all duration-300 group"
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${cert.color} rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={cert.icon} size={24} color="white" />
                </div>
                <h4 className="font-semibold text-foreground mb-2">{cert.name}</h4>
                <p className="text-sm text-text-secondary">{cert.issuer}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Strategic Partnerships */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-bold text-foreground text-center mb-12">
            Strategic Technology Partnerships
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnerships.map((partner, index) => (
              <motion.div
                key={partner.name}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-2xl p-8 hover:shadow-brand-lg transition-all duration-300 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <Icon name={partner.logo} size={20} color="white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <h4 className="font-bold text-foreground">{partner.name}</h4>
                      <span className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs font-medium">
                        {partner.type}
                      </span>
                    </div>
                    <p className="text-text-secondary text-sm">{partner.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Trust Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-success/10 rounded-2xl p-8 md:p-12">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
              Ready to Deliver Excellence
            </h3>
            <p className="text-lg text-text-secondary mb-8 max-w-2xl mx-auto">
              Our certifications and partnerships demonstrate our technical capabilities and commitment to delivering exceptional results for every client.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-8">
              <div className="flex items-center space-x-2 text-success">
                <Icon name="Shield" size={20} />
                <span className="font-medium">Security Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-primary">
                <Icon name="Award" size={20} />
                <span className="font-medium">Industry Certified</span>
              </div>
              <div className="flex items-center space-x-2 text-accent">
                <Icon name="Users" size={20} />
                <span className="font-medium">Client Focused</span>
              </div>
              <div className="flex items-center space-x-2 text-warning">
                <Icon name="Zap" size={20} />
                <span className="font-medium">Innovation Driven</span>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProof;